import React, { useState, useRef, useEffect, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useCart } from '../../contexts/CartContext';
import { useProducts } from '../../contexts/ProductContext';
import { COLLECTIONS, COLLECTION_INFO, ProductCollection } from '../../types/collection';
import { extractCategoriesFromVariants, extractGalwayCategories } from '../../utils/categoryUtils';
import { getUnifiedCategories } from '../../utils/categoryMapping';
import './Navbar.css';

const Navbar: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { totalItems } = useCart();
  const { currentCollection, setCollection, searchTerm, setSearchTerm, trophiesVariants, galwayProducts } = useProducts();
  const [shopDropdownOpen, setShopDropdownOpen] = useState(false);
  const shopDropdownRef = useRef<HTMLDivElement>(null);
  const dropdownTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Get unified categories from both collections
  const allCategories = useMemo(() => {
    const trophiesCategories = extractCategoriesFromVariants(trophiesVariants);
    const galwayCategories = extractGalwayCategories(galwayProducts);
    return getUnifiedCategories(trophiesCategories, galwayCategories);
  }, [trophiesVariants, galwayProducts]);
  const dropdownStaggerStepMs = allCategories.length > 1 ? 6000 / (allCategories.length - 1) : 0;

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchTerm(value);

    // Navigate to home if not already there when user starts typing
    if (value && location.pathname !== '/home' && location.pathname !== '/') {
      navigate('/');
    }
  };

  const navLinks = [
    { path: '/', label: 'HOME' },
    { path: '/gallery', label: 'GALLERY' },
    { path: '/orders', label: 'ORDERS' },
    { path: '/profile', label: 'PROFILE' },
    { path: '/about', label: 'ABOUT' },
  ];

  const isActive = (path: string) => {
    if (path === '/' || path === '/home') {
      return location.pathname === '/home' || location.pathname === '/';
    }
    return location.pathname.startsWith(path);
  };

  const isShopActive = () => {
    return location.pathname.startsWith('/shop') || 
           location.pathname.startsWith('/trophies') ||
           location.pathname.startsWith('/medals') ||
           location.pathname.startsWith('/plaques');
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (shopDropdownRef.current && !shopDropdownRef.current.contains(event.target as Node)) {
        setShopDropdownOpen(false);
      }
    };

    if (shopDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      if (dropdownTimeoutRef.current) {
        clearTimeout(dropdownTimeoutRef.current);
      }
    };
  }, [shopDropdownOpen]);

  const handleMouseEnter = () => {
    if (dropdownTimeoutRef.current) {
      clearTimeout(dropdownTimeoutRef.current);
      dropdownTimeoutRef.current = null;
    }
    setShopDropdownOpen(true);
  };

  const handleMouseLeave = () => {
    // Add small delay before closing to allow movement to dropdown
    dropdownTimeoutRef.current = setTimeout(() => {
      setShopDropdownOpen(false);
    }, 150);
  };

  const handleDropdownMouseEnter = () => {
    // Keep dropdown open when hovering over it
    if (dropdownTimeoutRef.current) {
      clearTimeout(dropdownTimeoutRef.current);
      dropdownTimeoutRef.current = null;
    }
    setShopDropdownOpen(true);
  };

  const handleDropdownMouseLeave = () => {
    // Add delay before closing
    dropdownTimeoutRef.current = setTimeout(() => {
      setShopDropdownOpen(false);
    }, 150);
  };

  const handleCollectionSwitch = (collection: typeof COLLECTIONS.TROPHIES_MEDALS | typeof COLLECTIONS.GALWAY_CRYSTAL) => {
    setCollection(collection);
    setShopDropdownOpen(false);
    navigate('/shop');
  };

  const handleCategoryClick = (category: string, collection: typeof COLLECTIONS.TROPHIES_MEDALS | typeof COLLECTIONS.GALWAY_CRYSTAL) => {
    setCollection(collection);
    setShopDropdownOpen(false);
    
    if (category === 'All') {
      navigate('/shop');
    } else if (collection === COLLECTIONS.TROPHIES_MEDALS) {
      // Map Trophies & Medals category names to routes
      const categoryRoutes: { [key: string]: string } = {
        'Trophies': '/trophies',
        'Medals': '/medals',
        'Plates': '/plaques',
      };
      
      const route = categoryRoutes[category];
      if (route) {
        navigate(route);
      } else {
        // For "Other" or unknown categories, navigate to shop with category filter
        navigate(`/shop?category=${encodeURIComponent(category)}`);
      }
    } else {
      // For Galway Crystal, navigate to shop with category filter
      navigate(`/shop?category=${encodeURIComponent(category)}`);
    }
  };

  const isCategoryActive = (category: string, collection: typeof COLLECTIONS.TROPHIES_MEDALS | typeof COLLECTIONS.GALWAY_CRYSTAL): boolean => {
    // Check if category matches URL parameter
    const urlCategory = new URLSearchParams(location.search).get('category');
    if (urlCategory === category) {
      return true;
    }
    
    if (category === 'All') {
      // "All" is active on /shop when no category is selected
      return location.pathname === '/shop' && !urlCategory;
    }
    
    // Check if it's a Trophies & Medals category with a specific route
    if (collection === COLLECTIONS.TROPHIES_MEDALS) {
      const categoryRoutes: { [key: string]: string } = {
        'Trophies': '/trophies',
        'Medals': '/medals',
        'Plates': '/plaques',
      };
      
      const route = categoryRoutes[category];
      return route ? location.pathname.startsWith(route) : false;
    }
    
    // For other categories, check URL parameter
    return false;
  };

  return (
    <nav className="navbar">
      <div className="navbar-content">
        {/* Logo */}
        <div className="navbar-logo" onClick={() => navigate('/')}>
          <img 
            src="/Untitled design (24).png" 
            alt="Quirke Trophies" 
            className="navbar-logo-image"
            onError={(e) => {
              // Fallback to old logo if new logo not found
              (e.target as HTMLImageElement).src = "/Untitled design (24).png";
            }}
          />
          <div className="logo-text">
            <span className="logo-title">Quirke</span>
            <span className="logo-subtitle">TROPHIES</span>
          </div>
        </div>

        {/* Center Navigation Links */}
        <nav className="navbar-center-nav">
          {navLinks.map((link) => (
            <button
              key={link.path}
              className={`navbar-nav-link ${isActive(link.path) ? 'navbar-nav-link-active' : ''}`}
              onClick={() => navigate(link.path)}
            >
              {link.label}
            </button>
          ))}
          
          {/* Shop Dropdown */}
          <div 
            className={`navbar-shop-dropdown-wrapper ${shopDropdownOpen ? 'navbar-shop-dropdown-wrapper-open' : ''}`}
            ref={shopDropdownRef}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
          >
            <button
              className={`navbar-nav-link navbar-shop-link ${isShopActive() ? 'navbar-nav-link-active' : ''}`}
              onClick={() => navigate('/shop')}
            >
              SHOP
              <svg 
                className="navbar-dropdown-arrow" 
                width="12" 
                height="12" 
                viewBox="0 0 12 12" 
                fill="none"
              >
                <path 
                  d="M3 4.5L6 7.5L9 4.5" 
                  stroke="currentColor" 
                  strokeWidth="1.5" 
                  strokeLinecap="round" 
                  strokeLinejoin="round"
                />
              </svg>
            </button>
            
            <div 
              className={`navbar-shop-dropdown ${shopDropdownOpen ? 'navbar-shop-dropdown-open' : ''}`}
              onMouseEnter={handleDropdownMouseEnter}
              onMouseLeave={handleDropdownMouseLeave}
            >
              <div className="dropdown-section">
                <div
                  className="dropdown-categories"
                  style={{ ['--stagger-step' as any]: `${dropdownStaggerStepMs}ms` }}
                >
                  {allCategories.map((category, index) => {
                    // Determine which collection this category belongs to
                    const isTrophiesCategory = ['Trophies', 'Medals', 'Plates', 'Other'].includes(category);
                    const collection = isTrophiesCategory ? COLLECTIONS.TROPHIES_MEDALS : COLLECTIONS.GALWAY_CRYSTAL;
                    
                    return (
                      <button
                        key={category}
                        className={`dropdown-category-btn ${isCategoryActive(category, collection) ? 'dropdown-category-btn-active' : ''}`}
                        onClick={() => handleCategoryClick(category, collection)}
                        style={{ ['--item-index' as any]: index }}
                      >
                        {category}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        </nav>

        {/* Right Section: Search, Contact, Engraving, Cart */}
        <div className="navbar-right-section">
          {/* Search Bar */}
          <div className="navbar-search">
            <img src="/assets/images/search.png" alt="Search" className="navbar-search-icon" />
            <input
              type="text"
              placeholder="Search..."
              className="navbar-search-input"
              value={searchTerm}
              onChange={handleSearchChange}
            />
          </div>

          {/* Contact Button */}
          <button 
            className={`navbar-action-button ${isActive('/contact') ? 'navbar-action-button-active' : ''}`}
            onClick={() => navigate('/contact')}
            aria-label="Contact"
          >
            Contact
          </button>

          {/* Engraving Button */}
          <button 
            className={`navbar-action-button ${location.pathname === '/contact' && location.search.includes('engraving') ? 'navbar-action-button-active' : ''}`}
            onClick={() => navigate('/contact?type=engraving')}
            aria-label="Engraving"
          >
            Engraving
          </button>

          {/* Cart Button */}
          <button className="navbar-cart-button" onClick={() => navigate('/cart')} aria-label="Shopping Cart">
            <span className="navbar-cart-text">Cart</span>
            {totalItems > 0 && (
              <span className="navbar-cart-badge">{totalItems > 99 ? '99+' : totalItems}</span>
            )}
          </button>
        </div>
      </div>
    </nav>
  );
};

export default Navbar;
